import { Routes } from '@angular/router';
import { AppLayoutComponent } from './layout/app.layout.component';
import { PermissionGuard } from './core/guards/permissions-guard';
import { AppRoutesEnum, PermissionTypeEnum } from './core/enums/enums';
import { contractWizardGuard } from '@pages/contracts/contract-wizard/contract-wizard.access';
import { SignInAsRoute } from '@core/constants';

export const routes: Routes = [
  { path: '', redirectTo: 'loads', pathMatch: 'full' },
  {
    path: 'forgotpassword',
    loadChildren: () => import('./pages/auth/forgotpasswordreset/forgotpassword.routes').then((m) => m.FORGOT_PASSWORD_ROUTES)
  },
  {
    path: '',
    component: AppLayoutComponent,
    children: [
      {
        path: 'home',
        // Tab access lives in dashboardTabGuard; the backend refuses the money regardless.
        data: {
          breadcrumb: 'Dashboard',
          crumbLink: false,
          pageTitle: 'Dashboard',
          permission: PermissionTypeEnum.CanHomeViewPage
        },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        loadChildren: () => import('./pages/dashboards/dashboard.routes').then((m) => m.DASHBOARD_ROUTES)
      },
      {
        path: 'quotes',
        data: { breadcrumb: 'Quotes', crumbLink: false },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        loadChildren: () => import('./pages/quotes/quotes.routes').then((m) => m.QUOTES_ROUTES)
      },
      {
        path: 'loads',
        data: { breadcrumb: 'Loads', routeId: AppRoutesEnum.LOAD_LIST },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        loadChildren: () => import('./pages/loads/loads.module').then((m) => m.LoadsModule)
      },
      {
        path: 'organizations',
        data: {
          breadcrumb: 'Organizations',
          permission: [PermissionTypeEnum.CanCompanyListPage, PermissionTypeEnum.CanLogisticsServiceProviderListPage, PermissionTypeEnum.CanMarketPartnerListPage]
        },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        loadChildren: () => import('./pages/companies/companies.module').then((m) => m.CompaniesModule)
      },
      {
        path: 'carriers',
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        data: { breadcrumb: 'Carriers', permission: PermissionTypeEnum.CanCarrierListPage },
        loadChildren: () => import('./pages/carriers/carriers.module').then((m) => m.CarriersModule)
      },
      {
        path: 'contracts',
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        data: { breadcrumb: 'Contracts', permission: PermissionTypeEnum.CanContractListPage },
        loadChildren: () => import('./pages/contracts/contracts.module').then((m) => m.ContractsModule)
      },
      // The page's old address, kept so bookmarks and links already sent still land.
      { path: 'carrier-reference', redirectTo: 'carrier-api-health', pathMatch: 'full' },
      {
        path: 'carrier-api-health',
        data: { breadcrumb: 'Carrier API Health' },
        canActivate: [PermissionGuard, contractWizardGuard],
        canActivateChild: [PermissionGuard],
        loadChildren: () => import('./pages/carrier-reference/carrier-reference.routes').then((m) => m.carrierReferenceRoutes)
      },
      {
        path: 'pro-numbers',
        // The same roles the menu entry shows it to; a direct URL otherwise reached the page and met a 403.
        data: { breadcrumb: 'PRO Numbers', roles: ['GlobalAdmin', 'TenantAdmin', 'ProviderAdmin'] },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        loadChildren: () => import('./pages/pro-numbers/pro-numbers.routes').then((m) => m.proNumbersRoutes)
      },
      {
        path: 'finance',
        data: { breadcrumb: 'Finance', crumbLink: false, permission: PermissionTypeEnum.CanFinanceViewPage },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        loadChildren: () => import('./pages/finance/finance.routes').then((m) => m.financeRoutes)
      },
      {
        path: 'users',
        // TODO: switch to `permission: PermissionTypeEnum.CanUserManageListPage` once IAM seeds it (see approved plan)
        data: { breadcrumb: 'Manage Users', roles: ['GlobalAdmin', 'TenantAdmin', 'Admin', 'ProviderAdmin'] },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        loadChildren: () => import('./pages/users/users.routes').then((m) => m.usersRoutes)
      },
      {
        path: 'esign-requests',
        // LSPs (ProviderAdmin) see the page too; the backend scopes their rows to their own customers.
        data: { breadcrumb: 'E-Signature Requests', roles: ['GlobalAdmin', 'TenantAdmin', 'ProviderAdmin'] },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        loadChildren: () => import('./pages/esign-requests/esign-requests.routes').then((m) => m.esignRequestsRoutes)
      },
      {
        // The tenant tier of the Theme screen (US 3522). Global Admin only: it is the domain's own branding,
        // and it carries the login-screen copy every other tier sits below.
        path: 'tenant-theme',
        data: { breadcrumb: 'Theme', roles: ['GlobalAdmin'] },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
        loadChildren: () => import('./pages/tenant-theme/tenant-theme.routes').then((m) => m.tenantThemeRoutes)
      },
      {
        path: 'demos',
        loadChildren: () => import('./pages/demos/demos.module').then((m) => m.DemosModule)
      },
      {
        // v2 answers on `/quotes` and `/loads` now (decision I78); what is left here is the
        // development workbench, and it does not exist in a production build.
        path: 'quote-v2',
        data: { breadcrumb: 'Quote v2' },
        loadChildren: () => import('./pages/quote-v2/quote-v2.routes').then((m) => m.QUOTE_V2_ROUTES)
      }
    ]
  },
  {
    path: 'login',
    loadChildren: () => import('./pages/auth/auth.module').then((m) => m.AuthModule)
  },
  {
    // Where a new "Sign in as" tab opens (US 3635): forwards to that user's own home screen.
    path: SignInAsRoute.Start,
    loadComponent: () => import('./pages/auth/signed-in-as-start/signed-in-as-start.component').then((m) => m.SignedInAsStartComponent)
  },
  {
    // Where a "Sign in as" tab lands when its session ends (US 3635); public, because that tab holds no session any more.
    path: SignInAsRoute.Ended,
    loadComponent: () => import('./pages/auth/signed-in-as-ended/signed-in-as-ended.component').then((m) => m.SignedInAsEndedComponent)
  },
  {
    path: 'accessdenied',
    loadComponent: () => import('./pages/auth/accessdenied/accessdenied.component').then((m) => m.AccessdeniedComponent)
  },
  {
    path: 'error',
    loadChildren: () => import('./pages/auth/error/error-routing.module').then((m) => m.ErrorRoutingModule)
  },
  {
    path: 'not-found',
    loadChildren: () => import('./pages/auth/not-found/not-found-routing.module').then((m) => m.NotFoundRoutingModule)
  },
  {
    path: '**',
    loadChildren: () => import('./pages/auth/not-found/not-found-routing.module').then((m) => m.NotFoundRoutingModule)
  },
];
